import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Search, Building, Users, Layers } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global search that indexes every plan group + plan + property name + manager.
 * Selecting a result navigates to the Plan Group Deep Dive for that group.
 */
const GlobalSearch = ({ onNavigate }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Load index once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/plan-group-deep-dive/list`);
        if (!cancelled) setGroups(res.data.groups || []);
      } catch (e) {
        // Search just won't return results until the API recovers.
        console.warn('Global search index unavailable:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ⌘K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Click outside closes
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return groups
      .filter(g =>
        (g.basePlan || '').toLowerCase().includes(q) ||
        (g.propertyName || '').toLowerCase().includes(q) ||
        (g.primaryManager || '').toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [query, groups]);

  const handleSelect = useCallback((group) => {
    setOpen(false);
    setQuery('');
    if (onNavigate) onNavigate(group.basePlan);
  }, [onNavigate]);

  return (
    <div ref={containerRef} className="relative hidden md:block">
      <div className="flex items-center rounded-lg px-3 py-1.5 bg-[#F5F5F5] border border-[#E5E5E5] focus-within:border-[#171717] transition-colors">
        <Search className="w-3.5 h-3.5 mr-2 text-[#A3A3A3]" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          type="text"
          placeholder="Search plans, properties, managers…"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          className="bg-transparent text-xs focus:outline-none w-56 text-[#171717] placeholder-[#A3A3A3]"
        />
        <kbd className="ml-2 px-1.5 py-0.5 text-[9px] font-medium rounded bg-white border border-[#E5E5E5] text-[#A3A3A3]">⌘K</kbd>
      </div>

      {open && (query.trim() || groups.length > 0) && (
        <div data-testid="global-search-results" className="absolute top-full right-0 mt-1.5 w-[420px] bg-white border border-[#E5E5E5] rounded-[10px] shadow-xl z-50 max-h-[480px] overflow-hidden">
          {query.trim() === '' ? (
            <p className="px-4 py-4 text-xs text-[#A3A3A3] text-center">Type to search across all plan groups, properties, and managers…</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-4 text-xs text-[#A3A3A3] text-center">No matches for "{query}"</p>
          ) : (
            <ul className="overflow-y-auto max-h-[440px]">
              <li className="px-4 py-2 text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3] bg-[#FAFAFA] border-b border-[#E5E5E5]">
                {results.length} result{results.length > 1 ? 's' : ''}
              </li>
              {results.map(g => (
                <li key={g.basePlan}>
                  <button
                    data-testid={`global-search-result-${g.basePlan}`}
                    onClick={() => handleSelect(g)}
                    className="w-full px-4 py-2.5 text-left hover:bg-[#F8F9FA] border-b border-[#F5F5F5] last:border-0 flex items-center gap-3"
                  >
                    <Building className="w-4 h-4 text-[#A3A3A3] flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#171717] truncate">{g.propertyName || g.basePlan}</p>
                      <p className="text-[10px] text-[#737373] font-mono">
                        <span>{g.basePlan}</span>
                        {g.primaryManager && <span> · {g.primaryManager}</span>}
                        {g.planCount > 1 && <span> · {g.planCount} plans</span>}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] font-mono text-[#525252]">${Math.round(g.totalRevenue).toLocaleString()}</p>
                      <p className="text-[10px] text-[#A3A3A3]">{(g.margin || 0).toFixed(0)}% margin</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
